import { useEffect, useRef } from 'react';
import { clientRpc, clientToken } from './clientAccess';

export type ClientViewStats = { quote_id: string; client_view_count: number; client_last_viewed_at: string };
const timestamp = (value?: string | null) => {
  const time = value ? Date.parse(value) : 0;
  return Number.isFinite(time) ? time : 0;
};
export const compareClientLastViewed = (a: any, b: any) => timestamp(b.lastViewedAt) - timestamp(a.lastViewedAt);
export function mergeClientViews<T extends { id: string; viewCount?: number; lastViewedAt?: string | null }>(quotes: T[], stats: ClientViewStats[]): T[] {
  const byId = new Map(stats.map(row => [row.quote_id, row]));
  return quotes.map(quote => {
    const row = byId.get(quote.id);
    return row ? { ...quote, viewCount: Number(quote.viewCount || 0) + Number(row.client_view_count),
      lastViewedAt: timestamp(row.client_last_viewed_at) > timestamp(quote.lastViewedAt) ? row.client_last_viewed_at : quote.lastViewedAt } : quote;
  });
}
export function formatClientViewNZ(value: string, now = new Date()): string {
  const date = new Date(value);
  if (!timestamp(value)) return 'Unknown';
  const day = new Intl.DateTimeFormat('en-NZ', { timeZone: 'Pacific/Auckland', year: 'numeric', month: '2-digit', day: '2-digit' });
  const options: Intl.DateTimeFormatOptions = { timeZone: 'Pacific/Auckland', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' };
  return day.format(date) === day.format(now) ? `Today at ${date.toLocaleTimeString('en-NZ', options)}`
    : date.toLocaleString('en-NZ', { ...options, year: 'numeric', month: 'short', day: 'numeric' });
}

// History cards receive the already-combined values from the same quote list.
export function formatClientHistoryNZ(value: string): string {
  if (!timestamp(value)) return 'Unknown';
  const date = new Date(value);
  const day = date.toLocaleDateString('en-NZ', { timeZone: 'Pacific/Auckland', day: '2-digit', month: '2-digit', year: 'numeric' });
  const hour = date.toLocaleTimeString('en-NZ', { timeZone: 'Pacific/Auckland', hour: 'numeric', hour12: true });
  return `${day}, ${hour}`;
}

// Only mounted by the client proposal route, AFTER a successful authorized read.
// Never mount this in QuotePreview, Admin, portal, editor, or a data-fetch helper.
export function useClientProposalView(quoteId: string | null) {
  const visit = useRef<{ key: string; id: string; sent: boolean } | null>(null);
  const token = clientToken();
  useEffect(() => {
    if (!quoteId || !token) return;
    const key = `${quoteId}:${token}`;
    if (visit.current?.key !== key) visit.current = { key, id: crypto.randomUUID(), sent: false };
    const current = visit.current;
    const params = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
    if (params.get('admin') === 'true' || hashParams.get('admin') === 'true' || navigator.webdriver ||
      /bot|crawler|spider|preview|headless|facebookexternalhit/i.test(navigator.userAgent)) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const eligible = () => document.visibilityState === 'visible' && document.hasFocus()
      && !(document as Document & { prerendering?: boolean }).prerendering;
    const schedule = () => {
      clearTimeout(timer);
      if (current.sent || !eligible()) return;
      // A visible, focused, hydrated proposal for two seconds is a practical
      // view signal, without forcing a client to click merely to be counted.
      timer = setTimeout(() => {
        if (!eligible() || current.sent) return;
        current.sent = true;
        void clientRpc('client_record_quote_view', quoteId, { p_visit_id: current.id })
          .catch(() => { console.warn('Client proposal view could not be recorded.'); });
      }, 2000);
    };
    const restore = (event: PageTransitionEvent) => {
      if (event.persisted) { current.id = crypto.randomUUID(); current.sent = false; schedule(); }
    };
    window.addEventListener('pageshow', restore);
    document.addEventListener('visibilitychange', schedule);
    window.addEventListener('focus', schedule);
    window.addEventListener('blur', schedule);
    schedule();
    return () => {
      clearTimeout(timer);
      window.removeEventListener('pageshow', restore);
      document.removeEventListener('visibilitychange', schedule);
      window.removeEventListener('focus', schedule);
      window.removeEventListener('blur', schedule);
    };
  }, [quoteId, token]);
}
